const KEY_LINE = /^([^\s:][^:]*):(.*)$/;
const CHILD_LINE = /^\s+([^\s:][^:]*):(.*)$/;
const SEQUENCE_LINE = /^\s*-\s+(.*)$/;
/** An indented non-blank line — the continuation shape of a folded or literal scalar. */
const INDENTED_LINE = /^\s+\S/;
/** Markdown block starters that can never open a frontmatter line. */
const MARKDOWN_BLOCK_LINE = /^(?:>|\||`|\*\s)/;
/**
 * An ATX heading of level 2 or deeper — the fingerprint of the corruption this guard exists for. A
 * rich-text round-trip reads a closing `---` as a setext underline, so the last frontmatter fields
 * come back as `## type: note`.
 */
const MARKDOWN_HEADING_LINE = /^#{2,}\s/;

/** True when the line's shape is one the server parser accepts inside a block. */
function hasFieldShape(line: string): boolean {
	return KEY_LINE.test(line) || CHILD_LINE.test(line) || SEQUENCE_LINE.test(line) || INDENTED_LINE.test(line);
}

/**
 * True when `line` could plausibly sit inside a frontmatter block.
 *
 * This is what bounds `splitFrontmatter`'s search for the closing fence, so it decides how far a
 * document with a damaged block is allowed to be swallowed.
 *
 * A single `#` is neutral: a full-line YAML comment and a markdown heading are textually identical,
 * and the server parser has always skipped them. `##` and deeper is rejected — it cannot be anything
 * but markdown, and it is exactly what a destroyed closing fence leaves behind.
 *
 * Ported from Clarity `apps/web/src/shared/utils/frontmatter.ts`.
 */
export function isFrontmatterLine(line: string): boolean {
	const bare = line.replace(/\r$/, '');
	const trimmed = bare.trim();

	if (trimmed === '') {
		return true;
	}

	if (MARKDOWN_HEADING_LINE.test(trimmed)) {
		return false;
	}

	if (trimmed.startsWith('#')) {
		return true;
	}

	if (MARKDOWN_BLOCK_LINE.test(trimmed)) {
		return false;
	}

	return hasFieldShape(bare);
}
